import { Link } from 'react-router-dom';

// Guide utilisateur statique : chaque fonctionnalité de CaissePilot,
// expliquée en détail avec le processus réellement mis en place (règles de
// gestion, étapes, validations). Volontairement pas de dépendance à l'API —
// c'est un mode d'emploi, pas un écran de données. Organisé par
// fonctionnalité, sans distinction de profil : tout le monde voit le même
// contenu, chacun y trouve ce qui le concerne.

interface Proc {
  label: string;
  path: string; // segment relatif sous /:tenantId
  intro: string;
  steps: string[];
  note?: string;
}

const PROCESSES: Proc[] = [
  {
    label: "Vue d'ensemble",
    path: 'overview',
    intro: "Le tableau de bord central : une photo en direct de tout le réseau de caisses, recalculée à chaque ouverture de la page.",
    steps: [
      'Trois totaux toujours affichés : le stock disponible au dépôt central, l\'en-cours chez Marjane (caisses parties mais pas encore revenues), et le cumul des caisses déclarées perdues depuis la mise en service.',
      'Un tableau des clients trié automatiquement du plus critique au moins critique : le client dont l\'autonomie est la plus faible (ou inconnue) apparaît en premier.',
      'Pour chaque client : son stock actuel par type de caisse, son autonomie en jours, sa cible de sécurité, et un badge si une promotion est active pour lui.',
      'Le nombre d\'alertes actives et le nombre de remises en attente de validation sont comptés en direct, à partir des mêmes règles que les pages Alertes et Remises.',
    ],
    note: "L'autonomie et la cible ne sont pas des réglages : elles sont recalculées à partir de l'historique réel des livraisons de chaque client (voir « Alertes » pour le détail du calcul).",
  },
  {
    label: 'Clients',
    path: 'clients',
    intro: "La fiche de chaque client (grossiste ou revendeur) qui reçoit des caisses.",
    steps: [
      'Chaque client a un identifiant, un nom, un contact (le « patron », son téléphone), et un nombre de jours de sécurité — le stock tampon qu\'il doit toujours avoir en réserve.',
      'Si aucun jour de sécurité n\'est saisi pour un client, la valeur par défaut de la société s\'applique (paramétrable dans Configuration).',
      'Une saisonnalité sur 12 mois peut être définie pour chaque client : un coefficient par mois qui ajuste sa cible de stock à la hausse ou à la baisse selon la période de l\'année. Elle peut être recalculée automatiquement à partir d\'au moins deux mois d\'historique de livraisons.',
      'Un client peut être placé en mode « promotion » : sa cible de sécurité est alors temporairement augmentée (facteur configurable) pour absorber un pic de vente, pendant une durée définie dans la configuration.',
    ],
  },
  {
    label: 'Alertes',
    path: 'alerts',
    intro: "Les situations qui demandent une action, calculées automatiquement — il n'y a rien à configurer client par client.",
    steps: [
      'Alerte « stock bas » : déclenchée quand l\'autonomie d\'un client (son stock actuel divisé par son besoin quotidien moyen) descend sous le seuil d\'alerte de la société. En dessous d\'un jour d\'autonomie, l\'alerte passe en critique plutôt qu\'en avertissement.',
      'Le besoin quotidien est calculé sur une fenêtre glissante (paramétrable, 7 jours par défaut) : c\'est la moyenne des caisses réellement livrées à ce client sur cette période, pas une estimation manuelle.',
      'Alerte « suspicion de perte » : déclenchée quand un client détient encore des caisses mais n\'a plus eu aucun mouvement depuis un certain nombre de jours (seuil de suspicion configurable) — signe possible de caisses oubliées ou non déclarées.',
      'Chaque alerte porte un message prêt à envoyer au client (par WhatsApp, via le connecteur configuré) — le texte est généré à partir d\'un modèle configurable, jamais rédigé à la main à chaque fois.',
    ],
  },
  {
    label: 'Rapports',
    path: 'reports',
    intro: "Trois rapports de contrôle, pour croiser les chiffres du système avec les déclarations externes et suivre un client sur plusieurs mois.",
    steps: [
      'Écart BL Marjane : pour un mois donné, compare les quantités déclarées par numéro de bon de livraison (import) aux remises réellement enregistrées dans CaissePilot sur la même période — un écart à zéro confirme que tout ce qui a été déclaré correspond à un mouvement système.',
      'Rapport client : pour un client et une liste de mois choisis, affiche mois par mois ce qu\'il a reçu, ce qu\'il a redéposé, et le stock qu\'il lui reste — utile pour vérifier son historique avant une relance ou un litige.',
      'Suivi fournisseurs importé (FRS) : les données brutes d\'un suivi fournisseurs déjà importé, affichées par date pour comparaison visuelle seulement — aucun mouvement comptable n\'y est rattaché, ce rapport ne modifie jamais le stock.',
    ],
  },
  {
    label: 'Remises',
    path: 'issues',
    intro: "Le processus de sortie de caisses du dépôt central vers un client, en deux temps : demande puis validation.",
    steps: [
      'Une quantité peut être suggérée automatiquement à partir du bon du jour : la suggestion est le maximum entre ce que demande le bon et ce qu\'il faut pour ramener le client à sa cible de sécurité après cette remise.',
      'La demande de remise est enregistrée avec le statut « en attente » — elle ne sort pas encore le stock du dépôt central.',
      'Une photo du bon ou du camion peut être jointe, avec une suggestion de comptage par IA ; la suggestion est toujours affichée comme « à vérifier », jamais enregistrée automatiquement.',
      'Le magasinier valide ensuite les remises en attente : à ce moment seulement, le stock central est contrôlé — si le stock disponible ne suffit pas, la validation échoue explicitement plutôt que de laisser le stock passer en négatif.',
      'Une remise en attente peut aussi être refusée (avec motif) : elle n\'aura alors jamais d\'effet sur le stock.',
      'Chaque demande porte une référence unique : renvoyer deux fois la même demande (par exemple après une coupure réseau) ne crée jamais de doublon.',
    ],
  },
  {
    label: 'Scanner camion',
    path: 'scantruck',
    intro: "Le comptage assisté par IA à partir d'une photo — toujours une suggestion, jamais une écriture automatique.",
    steps: [
      'Une photo du camion ou du bon est envoyée à un modèle de vision, qui propose un comptage (nombre de piles par type de caisse, ou lecture du bon).',
      'La suggestion est systématiquement marquée « à vérifier » : c\'est la personne sur le terrain qui confirme ou corrige la quantité avant tout enregistrement — le modèle ne décide jamais seul.',
      'Chaque société a un quota quotidien de lectures par IA, pour garder la fonctionnalité sous contrôle et maîtriser son coût.',
      'La fiabilité du modèle est mesurée en continu : chaque suggestion est comparée à la quantité finalement confirmée par l\'humain, ce qui permet de suivre objectivement sa précision dans le temps plutôt que de la supposer.',
    ],
  },
  {
    label: 'Réceptions',
    path: 'receptions',
    intro: "L'enregistrement du retour de caisses depuis Marjane vers le dépôt central.",
    steps: [
      'La quantité peut être saisie directement, ou calculée à partir d\'un nombre de piles complètes plus un reliquat (« caisses en vrac ») selon la capacité configurée par type de caisse.',
      'Une photo et une suggestion de comptage par IA peuvent être jointes, avec la même règle : suggestion affichée, jamais enregistrée seule.',
      'Chaque réception porte une référence unique pour éviter tout double comptage en cas de nouvel envoi du même formulaire.',
    ],
  },
  {
    label: 'Pertes',
    path: 'losses',
    intro: "La déclaration d'une perte de caisses — toujours explicite, jamais un simple ajustement silencieux.",
    steps: [
      'Trois catégories possibles : perte chez un client, perte chez Marjane, ou perte interne (au dépôt central).',
      'Une perte chez un client exige de préciser lequel ; les deux autres catégories ne sont pas rattachées à un client.',
      'Un motif peut être ajouté à chaque déclaration, pour garder une trace de la cause au moment des faits plutôt que de la reconstituer plus tard.',
      'Chaque perte déclarée est une écriture du grand livre à part entière : le stock total du parc reste cohérent, l\'écart est visible et daté plutôt que de disparaître.',
    ],
  },
  {
    label: 'Inventaire',
    path: 'inventories',
    intro: "Le comptage physique du stock central, rapproché automatiquement du stock théorique du système.",
    steps: [
      'Le compte physique de chaque type de caisse est saisi ; le système le compare au stock théorique qu\'il calcule à partir de tous les mouvements déjà enregistrés.',
      'Si le compte physique est inférieur au stock théorique, l\'écart est automatiquement enregistré comme une perte interne, avec la mention « écart d\'inventaire ».',
      'Si le compte physique est supérieur, l\'écart est enregistré comme un surplus (ajustement d\'entrée), avec la mention « surplus d\'inventaire ».',
      'Un inventaire porte un horodatage (« créneau » du matin ou autre) et une référence : relancer le même inventaire (ref identique) ne crée jamais un deuxième ajustement.',
    ],
  },
  {
    label: 'Lavage (ONSSA)',
    path: 'hygiene',
    intro: "La traçabilité sanitaire du lavage des caisses, exigée par la réglementation ONSSA : chaque lot lavé est rattaché à la réception dont il provient.",
    steps: [
      'Un lot ne peut être déclaré que rattaché à une réception existante portant une référence : la liste des réceptions disponibles est proposée directement dans le formulaire, avec leur date, leur camion et leurs quantités.',
      'La quantité lavée est saisie par type de caisse (PM, GM), avec la date, le nom du responsable du lavage et, si besoin, une photo du lot.',
      'Chaque lot reçoit automatiquement un numéro unique et continu, conservé indéfiniment pour la traçabilité — c\'est ce numéro qui est repris dans la déclaration mensuelle de Conformité.',
      'La déclaration d\'un lot fonctionne aussi hors connexion : elle est mise en file d\'attente et le numéro de lot n\'est attribué qu\'à la synchronisation, jamais en double.',
    ],
  },
  {
    label: 'Mouvements',
    path: 'movements',
    intro: "L'historique complet et filtrable de toutes les écritures du grand livre — remises, réceptions, pertes, ajustements.",
    steps: [
      'Chaque mouvement est filtrable par client, par type d\'opération et par période, pour retrouver rapidement un événement précis.',
      'Un mouvement déjà validé (« posté ») ne se supprime jamais : il ne peut être annulé que par contre-passation, c\'est-à-dire par un second mouvement inverse qui référence l\'original et l\'annule, motif obligatoire.',
      'Cette règle garantit que l\'historique complet reste toujours consultable, y compris les erreurs corrigées : rien n\'est jamais réécrit ou effacé silencieusement.',
    ],
  },
  {
    label: 'Import Marjane',
    path: 'import',
    intro: "L'import en masse d'un fichier de décompte Marjane, pour éviter la ressaisie ligne par ligne.",
    steps: [
      'Le fichier est d\'abord analysé en aperçu : chaque ligne est reconnue avec son client, sa date et ses quantités, sans rien enregistrer encore.',
      'Les lignes ambiguës (client non reconnu, quantités douteuses) sont signalées « à vérifier » séparément, plutôt que d\'être importées à l\'aveugle.',
      'Une fois l\'aperçu validé, l\'import est confirmé (« commit ») : seules les lignes correctement reconnues sont enregistrées comme des décomptes (retours client vers Marjane).',
      'Relancer le même import ne crée jamais de doublon : chaque ligne déjà importée est reconnue par sa référence et simplement ignorée la deuxième fois.',
    ],
  },
  {
    label: 'Synchronisation',
    path: 'sync',
    intro: "Le suivi des actions saisies hors connexion (sur le terrain, sans réseau) et leur envoi au serveur dès que possible.",
    steps: [
      'Les réceptions, remises, pertes et lots de lavage saisis sans réseau sont stockés localement sur l\'appareil puis envoyés automatiquement dès le retour de la connexion — un bouton « Synchroniser maintenant » permet aussi de forcer l\'envoi.',
      'Chaque action en attente porte une référence unique posée au moment de la saisie : la renvoyer, même plusieurs fois après une coupure, ne crée jamais de doublon côté serveur.',
      'Si le serveur refuse une action à la synchronisation (par exemple parce que le stock n\'est plus suffisant entre-temps), elle passe en erreur et n\'est jamais appliquée silencieusement : il faut la corriger et réessayer, ou l\'abandonner explicitement.',
      'La liste des actions en attente et en erreur se rafraîchit automatiquement toutes les quelques secondes, sans avoir à recharger la page.',
    ],
  },
  {
    label: 'Factures',
    path: 'invoices',
    intro: "La facturation mensuelle par client, calculée automatiquement à partir des jours de location et des pertes constatées.",
    steps: [
      'Le nombre de « jours-caisses » loués par un client est calculé jour par jour sur le mois, à partir du solde réel de caisses détenues chaque jour — pas d\'un simple stock moyen approximatif.',
      'Les caisses perdues chez ce client sur la période sont ajoutées à la facture à leur prix de perte configuré, en plus de la location.',
      'Un aperçu de facture peut être consulté à tout moment ; l\'émission définitive (avec numéro de facture) n\'est autorisée qu\'après la clôture du mois, sauf forçage explicite.',
      'Une fois émise, une facture porte un numéro continu et sans trou, et ne peut pas être émise deux fois pour le même client et le même mois.',
      'Une facture émise peut être automatiquement transmise aux connecteurs comptables configurés (Odoo, export CSV, API REST) via la file d\'attente sortante.',
    ],
  },
  {
    label: 'ComptaPro',
    path: 'comptapro',
    intro: "La transmission des factures émises vers un logiciel de comptabilité externe (ComptaPro.ma, Odoo, ou une API REST générique).",
    steps: [
      'Un connecteur se crée une fois : son type (ComptaPro.ma, Odoo, REST), un nom, et sa configuration — pour ComptaPro.ma, seul le nom de la variable d\'environnement contenant la clé API est indiqué ici, jamais la clé elle-même, qui reste définie sur le serveur.',
      'Une case « Envoyer automatiquement chaque facture émise » détermine si chaque nouvelle facture part seule vers ce connecteur, ou si elle doit être renvoyée manuellement.',
      'Une facture déjà émise peut être renvoyée à tout moment vers un connecteur actif, en indiquant simplement son numéro.',
      'La file d\'envoi (« outbox ») garde la trace de chaque tentative : en attente, envoyée, ou en échec après plusieurs essais — un envoi en échec peut être relancé manuellement, il n\'est jamais abandonné silencieusement.',
    ],
  },
  {
    label: 'Utilisateurs',
    path: 'users',
    intro: "La gestion des comptes qui accèdent à l'application.",
    steps: [
      'Chaque utilisateur a un identifiant de connexion, un nom, un rôle (administrateur, manager, magasinier, chauffeur, ou patron client) et un code PIN.',
      'Un rôle « patron client » doit obligatoirement être rattaché à un client existant : ce compte ne verra jamais que le stock de ce client.',
      'Le code PIN d\'un utilisateur peut être réinitialisé à tout moment, ce qui déconnecte immédiatement tous ses appareils déjà connectés.',
      'Le rôle déterminé à la création fixe les droits (les « portées ») de ce compte sur toutes les pages de l\'application — il n\'y a pas de permissions à cocher une par une.',
    ],
  },
  {
    label: 'Configuration',
    path: 'config',
    intro: "Les réglages propres à la société : tout ce qui ajuste le comportement des calculs automatiques.",
    steps: [
      'Les types de caisses (par exemple petit et grand modèle) : leur libellé, leur capacité par pile, leur prix de location par jour et leur prix en cas de perte.',
      'Les seuils d\'alerte : le nombre de jours d\'autonomie sous lequel un client passe en alerte, la fenêtre de calcul du besoin quotidien, et le délai sans mouvement qui déclenche une suspicion de perte.',
      'Les réglages de sécurité et de promotion par défaut : le nombre de jours de sécurité appliqué si un client n\'en a pas de spécifique, le facteur et la durée du mode promotion.',
      'Les modèles de messages envoyés aux clients (alerte, bilan quotidien), et l\'activation ou non de la lecture par IA avec son quota quotidien et la durée de conservation des photos.',
    ],
    note: "Les types de caisses ne se suppriment jamais depuis la configuration, seulement ajoutent ou modifient : l'historique des mouvements passés en dépend.",
  },
  {
    label: 'Conformité',
    path: 'legal',
    intro: "Les échéances légales à surveiller et la déclaration mensuelle signée, qui rassemble les chiffres de traçabilité exigés par la réglementation.",
    steps: [
      'Les échéances légales (ponctuelles, mensuelles ou annuelles) sont définies une fois avec un rappel configurable en nombre de jours avant leur date — celles des 60 prochains jours sont mises en avant automatiquement.',
      'La déclaration mensuelle rassemble pour un mois choisi trois totaux calculés automatiquement à partir des mouvements déjà enregistrés : les lots de lavage ONSSA, les réceptions, et les remises aux clients.',
      'Une déclaration reste un brouillon recalculé à chaque consultation jusqu\'à ce qu\'elle soit signée : la signature la fige définitivement, elle ne peut plus être régénérée après coup.',
      'Une déclaration, signée ou non, peut être téléchargée en PDF à tout moment, pour archivage ou transmission à l\'inspection.',
    ],
  },
  {
    label: 'Mon stock',
    path: 'my-stock',
    intro: "La vue réservée à un compte « patron client » : uniquement son propre stock, jamais celui des autres clients.",
    steps: [
      'Le stock actuel par type de caisse, son autonomie en jours et sa cible de sécurité, calculés exactement de la même façon que dans la Vue d\'ensemble.',
      'Cette vue n\'expose que les données du client auquel ce compte est rattaché : la portée d\'accès du compte l\'empêche structurellement de voir un autre client.',
    ],
  },
];

export function Guide() {
  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Guide utilisateur</h1>
          <p className="sub2" style={{ marginTop: 4 }}>
            Chaque fonctionnalité de CaissePilot, avec le processus réellement mis en place derrière l'écran.
          </p>
        </div>
      </div>

      <div className="card">
        <h2>Le circuit complet d'une caisse, de bout en bout</h2>
        <ol className="guide-proc-steps">
          <li><strong>Réception</strong> — un camion de retour de Marjane décharge des caisses au dépôt central ; la quantité est enregistrée (saisie directe, ou piles complètes + reliquat), avec une suggestion de comptage par IA toujours à vérifier. Le stock central augmente.</li>
          <li><strong>Lavage (ONSSA)</strong> — les caisses reçues sont lavées par lots rattachés à leur réception d'origine, pour la traçabilité sanitaire.</li>
          <li><strong>Remise</strong> — une demande de sortie vers un client est enregistrée « en attente », puis validée par le magasinier : c'est seulement à la validation que le stock central diminue et que le stock du client augmente. Le Scanner camion peut assister le comptage par photo à cette étape comme à la réception.</li>
          <li><strong>Suivi</strong> — la Vue d'ensemble et les Alertes surveillent en continu l'autonomie de chaque client à partir de son historique réel de livraisons, et signalent les stocks bas ou les suspicions de perte.</li>
          <li><strong>Retour ou perte</strong> — les caisses redescendent vers Marjane (suivi via Mouvements et l'Import Marjane) ou, si elles manquent, une Perte est déclarée explicitement (chez le client, chez Marjane, ou en interne) — jamais un ajustement silencieux.</li>
          <li><strong>Inventaire</strong> — un comptage physique régulier du dépôt central est rapproché du stock théorique ; tout écart devient automatiquement une perte interne ou un surplus, daté et traçable.</li>
          <li><strong>Facturation</strong> — en fin de mois, les jours de location réellement consommés par chaque client et les pertes constatées chez lui deviennent une facture, transmissible automatiquement à la comptabilité via ComptaPro, Odoo ou une API REST.</li>
          <li><strong>Conformité</strong> — chaque mois, une déclaration signée rassemble les lots de lavage, les réceptions et les remises, pour répondre aux obligations réglementaires (ONSSA et échéances légales suivies dans Conformité).</li>
          <li><strong>Hors connexion</strong> — à n'importe quelle étape de terrain (réception, remise, perte, lavage), une saisie peut se faire sans réseau ; elle part en file d'attente (page Synchronisation) et se synchronise automatiquement au retour du réseau, sans jamais créer de doublon.</li>
        </ol>
        <p className="guide-proc-note">Chaque étape ci-dessus a sa fiche détaillée ci-dessous.</p>
      </div>

      <div className="card guide-role">
        <div className="guide-proc-list guide-proc-list-single">
          {PROCESSES.map((p) => (
            <div className="guide-proc guide-proc-detailed" key={p.path}>
              <Link to={p.path} className="guide-proc-title">{p.label}</Link>
              <p className="guide-proc-text" style={{ marginBottom: '0.6rem' }}>{p.intro}</p>
              <ul className="guide-proc-steps">
                {p.steps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
              {p.note && <p className="guide-proc-note">{p.note}</p>}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
